'use client'

import { createContext, Dispatch, ReactNode, SetStateAction, use, useEffect, useState, ReactElement } from 'react'
import { endpointRequiresStartDate } from '@/components/registration-check/question-item/_helpers/endpointRequiresStartDate'
import { AnswerResults, Endpoint, Question } from '@/types/questions'
import { Activity, ActivityStates } from '@/types/activities'
import { AppContext } from '@/providers/app-provider'
import { DictionaryContext } from '@lib-components'
import Logger from '@/helpers/logger'
import { ErrorMsg } from '@/constants/messageConstants'
import { StepIDs } from '@/constants/stepConstants'
import {
    RegistrationDataContext,
    RegistrationLine,
} from '@/providers/registration-data-provider/registration-data-provider'
import fetchQuestions from '@/services/rest/registration'

interface EndpointPerActivityCollection {
    [key: string]: Endpoint[]
}

interface RegistrationCheckProviderValue {
    questions: Question[]
    activities: Activity[]
    setActivities: Dispatch<SetStateAction<Activity[]>>
    setQuestions: Dispatch<SetStateAction<Question[]>>
    setIsSubmitting: Dispatch<SetStateAction<boolean>>
    getQuestions: () => Promise<void>
    isLoadingQuestions: boolean
    isSubmitting: boolean
    hasFetchError: boolean
    isAddActivityButtonVisible: boolean
    isStartRegistrationButtonVisible: boolean
    endpointsPerFormKey: EndpointPerActivityCollection
    setEndpointsPerFormKey: (formKey: string, endpoints: Endpoint[]) => void
    shouldAddActivityButtonBeVisible: () => boolean
}

export const RegistrationCheckContext = createContext<RegistrationCheckProviderValue>({
    questions: [],
    activities: [],
    setActivities: () => {},
    setQuestions: () => {},
    setIsSubmitting: () => {},
    getQuestions: async () => {},
    isLoadingQuestions: true,
    isSubmitting: false,
    hasFetchError: false,
    isAddActivityButtonVisible: false,
    isStartRegistrationButtonVisible: false,
    endpointsPerFormKey: {},
    shouldAddActivityButtonBeVisible: () => false,
    setEndpointsPerFormKey: (_formKey: string, _endpoints: Endpoint[]) => {},
})

interface Props {
    children: ReactNode
}

export const RegistrationCheckProvider = ({ children }: Props): ReactElement => {
    const [questions, setQuestions] = useState<Question[]>([])
    const [activities, setActivities] = useState<Activity[]>([])
    const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(true)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [hasFetchError, setHasFetchError] = useState<boolean>(false)
    const { goToStep } = use(AppContext)
    const { getText } = use(DictionaryContext)
    const { updateRegistrationLines } = use(RegistrationDataContext)
    const [endpointsPerFormKey, setEndpointsPerFormKey] = useState<EndpointPerActivityCollection>({})
    const [isAddActivityButtonVisible, setAddActivityButtonVisibility] = useState<boolean>(false)
    const [isStartRegistrationButtonVisible, setStartRegistrationButtonVisibility] = useState<boolean>(false)

    const getQuestions = async () => {
        setIsLoadingQuestions(true)
        try {
            const response = await fetchQuestions()
            const parsedJSONQuestions = JSON.parse(response.data.jsonData) as Question[]
            setQuestions(parsedJSONQuestions)
        } catch {
            setHasFetchError(true)
            Logger.error(getText(ErrorMsg.FETCH_QUESTIONS))
        } finally {
            setIsLoadingQuestions(false)
        }
    }

    useEffect(() => {
        goToStep(StepIDs.ONE)
        getQuestions()
    }, [])

    const mapEndpointsToRegistrationLines = (
        endpointsPerFormKey: EndpointPerActivityCollection,
    ): RegistrationLine[] => {
        let registrationLines = Object.values(endpointsPerFormKey).flatMap((endpoints) =>
            endpoints
                .filter((endpoint) => endpoint.result.type === AnswerResults.ENDPOINT_ALLOW_REGISTRATION)
                .map((endpoint) => ({
                    registrationKey: endpoint.result.registrationKey,
                    endpointKey: endpoint.endpointKey,
                    startDate: endpoint.startDate,
                })),
        )

        return registrationLines
    }

    const setEndpointsPerFormKeyHandler = (formKey: string, endpoints: Endpoint[]) => {
        const temporaryEndpointsPerFormKey = endpointsPerFormKey
        temporaryEndpointsPerFormKey[formKey] = endpoints

        setEndpointsPerFormKey(temporaryEndpointsPerFormKey)
        updateRegistrationLines(mapEndpointsToRegistrationLines(temporaryEndpointsPerFormKey))
        setAddActivityButtonVisibility(shouldAddActivityButtonBeVisible())
        setStartRegistrationButtonVisibility(shouldStartRegistrationButtonBeVisible())
    }

    const hasActivitiesInProgress = (): boolean =>
        activities.some((activity) => activity.status === ActivityStates.IN_PROGRESS)

    const hasUnfilledStartDates = (): boolean => {
        for (const [, endpoints] of Object.entries(endpointsPerFormKey)) {
            if (
                endpoints.some(
                    ({ startDate, result, departmentType }) =>
                        result.type === AnswerResults.ENDPOINT_ALLOW_REGISTRATION &&
                        endpointRequiresStartDate(departmentType) &&
                        !startDate,
                )
            )
                return true
        }
    }

    const shouldAddActivityButtonBeVisible = (): boolean => {
        return activities.length >= 0 && !hasActivitiesInProgress() && !hasUnfilledStartDates()
    }

    const shouldStartRegistrationButtonBeVisible = (): boolean => {
        const registrationCheckResults: AnswerResults[] = activities.flatMap((activity) => activity.answerResults)

        return (
            registrationCheckResults.includes(AnswerResults.ENDPOINT_ALLOW_REGISTRATION) &&
            !hasActivitiesInProgress() &&
            !hasUnfilledStartDates()
        )
    }

    useEffect(() => {
        setAddActivityButtonVisibility(shouldAddActivityButtonBeVisible())
        setStartRegistrationButtonVisibility(shouldStartRegistrationButtonBeVisible())
    }, [activities])

    const providerValue: RegistrationCheckProviderValue = {
        questions,
        activities,
        setActivities,
        setQuestions,
        setIsSubmitting,
        getQuestions,
        isLoadingQuestions,
        isSubmitting,
        hasFetchError,
        endpointsPerFormKey,
        shouldAddActivityButtonBeVisible,
        setEndpointsPerFormKey: setEndpointsPerFormKeyHandler,
        isAddActivityButtonVisible,
        isStartRegistrationButtonVisible,
    }

    return <RegistrationCheckContext.Provider value={providerValue}>{children}</RegistrationCheckContext.Provider>
}
